import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { Button, Input } from "../components/ui";
import BrandFooter, { useBranding } from "../components/BrandFooter";
import { useApp } from "../context/AppContext";
import { tp } from "../i18n";
import { KeyRound, MailCheck, CreditCard, Loader2, ShieldCheck } from "lucide-react";

export default function ClaimCode() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const branding = useBranding();
  const { lang } = useApp();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);   // {sent_to, app_type}
  const [unpaid, setUnpaid] = useState(null);    // {license_id, app_type}
  const [verifying, setVerifying] = useState(!!sessionId);

  const claim = async (e, em, ph) => {
    if (e) e.preventDefault();
    setErr(""); setResult(null); setUnpaid(null); setBusy(true);
    try {
      const { data } = await client.post("/licenses/claim-code", { email: (em ?? email).trim(), phone: (ph ?? phone).trim() });
      if (data.released) setResult(data);
      else if (data.paid === false) setUnpaid(data);
      else setErr(tp("No instance was found for that email and phone. Please check the details your vendor used to register your application.", lang));
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
  };

  // On return from Stripe, settle (Stripe is source of truth) then auto-deliver the code
  useEffect(() => {
    if (!sessionId) return;
    const em = sessionStorage.getItem("claim_email") || "";
    const ph = sessionStorage.getItem("claim_phone") || "";
    setEmail(em); setPhone(ph);
    (async () => {
      try {
        await client.post(`/payments/settle/${sessionId}`);
        await claim(null, em, ph);
      } catch (e) { setErr(apiErr(e)); }
      setVerifying(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const payNow = async () => {
    setBusy(true);
    try {
      sessionStorage.setItem("claim_email", email.trim());
      sessionStorage.setItem("claim_phone", phone.trim());
      const { data } = await client.post("/licenses/claim-checkout", { email: email.trim(), phone: phone.trim(), origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setErr(apiErr(e)); setBusy(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center p-6" style={{ background: "var(--app-bg)" }}>
      <div className="w-full max-w-md glass rounded-2xl p-8 fadeup" data-testid="claim-card">
        <div className="mb-6 flex items-center gap-3">
          <img src="/brand-logo.png" alt="Full Option Family Office" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-head text-lg font-bold tracking-tight" style={{ color: "var(--logo)" }} data-testid="claim-suite-name">{branding?.suite_name || "FULL OPTION FAMILY OFFICE"}</div>
            <div className="font-head text-[11px] font-medium uppercase tracking-[0.18em] opacity-70">Activation Code</div>
          </div>
        </div>

        {verifying ? (
          <div className="flex items-center gap-2 text-sm" data-testid="claim-verifying"><Loader2 className="animate-spin" size={16} /> {tp("Verifying your payment…", lang)}</div>
        ) : result ? (
          <div className="space-y-3" data-testid="claim-success">
            <div className="flex items-center gap-2 text-[#24A148]"><MailCheck size={22} /> <span className="font-head text-lg">{tp("Code on its way", lang)}</span></div>
            <p className="text-sm opacity-80">{tp("Your activation code for the", lang)} <b>{result.app_type}</b> {tp("application has been sent to your verified contacts:", lang)}</p>
            <ul className="text-sm opacity-70"><li>{tp("Email", lang)}: {result.sent_to.email}</li><li>SMS: {result.sent_to.phone}</li></ul>
            <p className="text-xs opacity-60">{tp("For your security, the code is only delivered to the email and phone on file — never shown here. Enter it once in your application to unlock all features.", lang)}</p>
          </div>
        ) : (
          <>
            <h1 className="mb-1 font-head text-2xl flex items-center gap-2"><KeyRound size={22} /> {tp("Get your code", lang)}</h1>
            <p className="mb-5 text-sm opacity-60">{tp("Enter the email and phone used to register your application. We'll send your activation code to those verified contacts — only after payment is confirmed.", lang)}</p>
            <form onSubmit={claim} className="space-y-4">
              <Input label={tp("Email", lang)} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="claim-email" autoFocus />
              <Input label={tp("Phone", lang)} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="claim-phone" />
              {err && <p className="text-sm text-[#DA1E28]" data-testid="claim-error">{err}</p>}
              {unpaid && (
                <div className="space-y-2 rounded-lg border border-[#F1C21B]/50 bg-[#F1C21B]/10 p-3" data-testid="claim-unpaid">
                  <p className="flex items-center gap-2 text-sm"><ShieldCheck size={16} /> {tp("No verified payment found for this instance yet.", lang)}</p>
                  <Button type="button" onClick={payNow} disabled={busy} className="w-full" data-testid="claim-pay">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} {tp("Pay now to unlock", lang)}
                  </Button>
                </div>
              )}
              <Button type="submit" disabled={busy || !email || !phone} className="w-full" data-testid="claim-submit">
                {busy ? tp("Checking…", lang) : tp("Send my code", lang)}
              </Button>
            </form>
          </>
        )}
        <div className="mt-6 text-center text-xs opacity-50"><Link to="/login" className="hover:underline">{tp("← Back to sign in", lang)}</Link></div>
      </div>
      <BrandFooter />
    </div>
  );
}
